var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useState, useEffect, useCallback, useMemo, useRef, } from "react";
import * as pc from "playcanvas";
import { useSelectionRectangleState, } from "./selection-rectangle-state";
import { EntityType, dragBehaviors, isDraggable, } from "./drag-behaviors";
import { useAppSelector, useAppDispatch, selectors, actions } from "store";
import { useIsKeyPressed } from "hooks/is-key-pressed";
import { boxIdFromEntityName } from "3d/cardboard-box-entities";
import { GRIP_FRAME_ENTITY_NAME } from "3d/grip-frame";
import { computeCupGridFill } from "3d/cup-grid-fill";
export { EntityType } from "./drag-behaviors";
/* How far the pointer has to travel before a press counts as a drag rather than
 * a click. Without it the slightest jitter while clicking moves the entity, and
 * for a cardboard box that silently turns the pallet row into a custom layout
 * (see `markLayerAsCustom`), losing the selected pallet preset. */
var dragThresholdPixels = 4;
export var usePicker = function (app, canvasRef) {
    var _a = __read(useState(null), 2), picker = _a[0], setPicker = _a[1];
    var _b = __read(useState(null), 2), picked = _b[0], setPicked = _b[1];
    var _c = __read(useState(false), 2), isEntityMoving = _c[0], setIsEntityMoving = _c[1];
    var _d = __read(useState(undefined), 2), hoveredId = _d[0], setHoveredId = _d[1];
    var _e = __read(useState(null), 2), initialY = _e[0], setInitialY = _e[1];
    // Offset between the picked entity's origin and the pointer's world position
    // at mouse-down. Kept constant during the drag so the entity follows the
    // pointer without snapping its origin to it (important for boxes, whose
    // origin is a far-away corner).
    var _f = __read(useState({
        x: 0,
        z: 0,
    }), 2), grabOffset = _f[0], setGrabOffset = _f[1];
    // Where the picked entity stood at mouse-down. Only the frame needs it: it
    // has no stored position of its own, so the drag is persisted as the offset
    // the frame ended up moving.
    var _g = __read(useState(null), 2), dragStart = _g[0], setDragStart = _g[1];
    // Where the pointer was pressed down, in screen space, so a drag can be told
    // apart from a click (see `dragThresholdPixels`).
    var _h = __read(useState(null), 2), pointerDownScreen = _h[0], setPointerDownScreen = _h[1];
    var _j = __read(useState(false), 2), isDisabled = _j[0], setIsDisabled = _j[1];
    var nodes = useAppSelector(selectors.selectNodes);
    var edges = useAppSelector(selectors.selectEdges);
    var attachments = useAppSelector(selectors.selectAttachments);
    /* With multiple boxes off, the single cardboard box is just the object being
     * lifted — like the object in every other application it is not something to
     * select or drag, so its entities are not picked. */
    var multipleBoxesEnabled = useAppSelector(selectors.selectMultipleBoxesEnabled);
    /* The box layers stand stacked and only the active layer is being worked
     * on. The other layer is either hidden above it or solid scenery beneath
     * it, so its boxes are never picked. */
    var cardboardBoxes = useAppSelector(selectors.selectCardboardBoxes);
    var activeLayer = useAppSelector(selectors.selectActiveLayer);
    var activeLayerBoxIds = useMemo(function () {
        return new Set(cardboardBoxes
            .filter(function (box) { return box.layer === activeLayer; })
            .map(function (box) { return box.id; }));
    }, [cardboardBoxes, activeLayer]);
    var currentlySelectedId = useAppSelector(selectors.selectSelectedId);
    var currentlySelectedIds = useAppSelector(selectors.selectSelectedIds);
    var isMultiSelecting = useAppSelector(selectors.selectIsMultiSelecting);
    var draggedAttachmentId = useAppSelector(selectors.selectDraggedAttachmentId);
    /* While the fill tool is armed the scene is not picked at all: a click
     * places the previewed grid of cups instead (see `CupFillOverlay` for the
     * preview, `fillAreaWithGripPositions` for what gets created). */
    var cupFillLayout = useAppSelector(selectors.selectCupFillLayout);
    // Whether the current press began in the scene with the fill tool armed —
    // PlayCanvas reports presses from the whole window, and only a press on the
    // canvas may commit a fill.
    var cupFillPress = useRef(false);
    var dispatch = useAppDispatch();
    var _k = useSelectionRectangleState(), selectionRectangle = _k.selectionRectangle, startSelection = _k.startSelection, updateSelection = _k.updateSelection, stopSelection = _k.stopSelection;
    var ctrlKeyPressed = useIsKeyPressed("Control");
    var metaKeyPressed = useIsKeyPressed("Meta"); // command key on mac
    var multiSelectKeyPressed = ctrlKeyPressed || metaKeyPressed;
    /**
     * Returns the picked entity based on the given mesh instance.
     */
    var getPickedEntity = useCallback(function (meshInstance) {
        var resolveBoxId = function (entityName) {
            if (!multipleBoxesEnabled)
                return undefined;
            var boxId = boxIdFromEntityName(entityName);
            return boxId !== undefined && activeLayerBoxIds.has(boxId)
                ? boxId
                : undefined;
        };
        var entity = meshInstance.node.parent;
        var id = entity.name;
        var boxId = resolveBoxId(id);
        while (!nodes[id] &&
            !attachments[id] &&
            !edges[id] &&
            boxId === undefined &&
            id !== GRIP_FRAME_ENTITY_NAME &&
            entity.parent) {
            entity = entity.parent;
            id = entity.name;
            boxId = resolveBoxId(id);
        }
        if (boxId !== undefined) {
            return { entity: entity, id: boxId, type: EntityType.BOX };
        }
        if (id === GRIP_FRAME_ENTITY_NAME) {
            return { entity: entity, id: id, type: EntityType.FRAME };
        }
        var type = nodes[id]
            ? EntityType.NODE
            : attachments[id]
                ? EntityType.ATTACHMENT
                : edges[id]
                    ? EntityType.EDGE
                    : EntityType.OTHER;
        return { entity: entity, id: id, type: type };
    }, [attachments, edges, nodes, multipleBoxesEnabled, activeLayerBoxIds]);
    /**
     * Picks unique entities in the scene based on the given coordinates.
     * Supports both single and multi-select, depending on if width and height are provided.
     * If width and height are provided, the function will pick entities within the given area.
     */
    var pick = useCallback(function (_a, camera) {
        var e_1, _b;
        var x = _a.x, y = _a.y, width = _a.width, height = _a.height;
        if (!picker || !app)
            return;
        preparePicker(picker, app, camera);
        var selected = picker.getSelection(x, y, width, height);
        var uniquePickedEntities = new Map();
        try {
            for (var selected_1 = __values(selected), selected_1_1 = selected_1.next(); !selected_1_1.done; selected_1_1 = selected_1.next()) {
                var meshInstance = selected_1_1.value;
                var picked_1 = getPickedEntity(meshInstance);
                uniquePickedEntities.set(picked_1.id, picked_1);
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (selected_1_1 && !selected_1_1.done && (_b = selected_1.return)) _b.call(selected_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
        return Array.from(uniquePickedEntities.values());
    }, [picker, app, getPickedEntity]);
    var setPickerDisabled = useCallback(function (disabled) {
        setIsDisabled(disabled);
        setHoveredId(undefined);
    }, []);
    var toggleEntitySelection = useCallback(function (toggledId) {
        if (currentlySelectedIds.includes(toggledId)) {
            dispatch(actions.selectEntities(currentlySelectedIds.filter(function (id) { return id !== toggledId; })));
            return;
        }
        dispatch(actions.selectEntities(__spreadArray(__spreadArray([], __read(currentlySelectedIds), false), [toggledId], false)));
    }, [currentlySelectedIds, dispatch]);
    /*
     * Everything the per-entity-kind drag behaviors need — see
     * `./drag-behaviors`, which owns what a drag MEANS per kind; this hook owns
     * the mouse plumbing.
     */
    var dragContext = useMemo(function () {
        return app
            ? {
                app: app,
                dispatch: dispatch,
                nodes: nodes,
                edges: edges,
                attachments: attachments,
                selectedIds: currentlySelectedIds,
                dragStart: dragStart,
            }
            : null;
    }, [app, dispatch, nodes, edges, attachments, currentlySelectedIds, dragStart]);
    var onMouseDown = useCallback(function (event, camera) {
        var _a, _b, _c;
        if (event.button === pc.MOUSEBUTTON_RIGHT)
            return; // the right mouse button is used for panning the scene
        if (!app || !picker || isPressOnIgnoredElement(event))
            return;
        if (cupFillLayout) {
            if (!isPointerOnCanvas(app, event))
                return;
            event.event.preventDefault();
            (_a = canvasRef === null || canvasRef === void 0 ? void 0 : canvasRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            cupFillPress.current = true;
            return;
        }
        // Prevent default event to avoid highlighting text when moving object
        event.event.preventDefault();
        // Focus the canvas to trigger blur events for input fields
        (_b = canvasRef === null || canvasRef === void 0 ? void 0 : canvasRef.current) === null || _b === void 0 ? void 0 : _b.focus();
        var pickedInfo = pick({ x: event.x, y: event.y }, camera);
        if (!(pickedInfo === null || pickedInfo === void 0 ? void 0 : pickedInfo.length))
            return;
        /* The frame spans the whole tool, so anything else under the pointer
         * wins: grabbing a cup still moves that cup on its own, exactly as
         * before, and the frame is only grabbed by its bars. */
        var _d = (_c = pickedInfo.find(function (picked) { return picked.type !== EntityType.FRAME; })) !== null && _c !== void 0 ? _c : pickedInfo[0], entity = _d.entity, id = _d.id, type = _d.type;
        if (!multiSelectKeyPressed && !currentlySelectedIds.includes(id)) {
            dispatch(actions.deselectEntities());
        }
        if (isDraggable(type)) {
            var pickedPosition = entity.getPosition();
            setInitialY(pickedPosition.y);
            setDragStart({ x: pickedPosition.x, z: pickedPosition.z });
            var grab = screenToWorldAtY(camera, event.x, event.y, pickedPosition.y);
            setGrabOffset({
                x: pickedPosition.x - grab.x,
                z: pickedPosition.z - grab.z,
            });
            setPointerDownScreen({ x: event.x, y: event.y });
            setPicked({ entity: entity, id: id, type: type });
            return;
        }
        switch (type) {
            case EntityType.ATTACHMENT: {
                // One attachment already selected -> Create tube
                if (currentlySelectedId && attachments[currentlySelectedId]) {
                    dispatch(actions.addEdge(currentlySelectedId, id));
                }
                else {
                    dispatch(actions.selectEntity(id));
                }
                break;
            }
            case EntityType.EDGE: {
                setPicked({ entity: entity, id: id, type: type });
                break;
            }
            default: {
                dispatch(actions.deselectEntities());
                startSelection(event);
                break;
            }
        }
    }, [
        app,
        picker,
        canvasRef,
        cupFillLayout,
        pick,
        multiSelectKeyPressed,
        currentlySelectedIds,
        dispatch,
        currentlySelectedId,
        attachments,
        startSelection,
    ]);
    var onMouseUp = useCallback(function (event, camera) {
        var _a;
        if (cupFillPress.current) {
            cupFillPress.current = false;
            // The tool may have been disarmed mid-press (Esc); then the release
            // means nothing.
            if (!cupFillLayout)
                return;
            var target = screenToWorldAtY(camera, event.x, event.y, cupFillLayout.planeY);
            var placed = computeCupGridFill({
                anchor: cupFillLayout.anchor,
                target: target,
                pitch: cupFillLayout.pitch,
                limit: cupFillLayout.limit,
            }).placed;
            dispatch(actions.fillAreaWithGripPositions(cupFillLayout.anchorNodeId, placed));
            return;
        }
        // user is dragging an edge/tube
        if (draggedAttachmentId) {
            var entityAtMouseLocation = pick({ x: event.x, y: event.y }, camera);
            var targetAttachmentId = (_a = entityAtMouseLocation === null || entityAtMouseLocation === void 0 ? void 0 : entityAtMouseLocation.find(function (p) { return p.type === EntityType.ATTACHMENT; })) === null || _a === void 0 ? void 0 : _a.id;
            var draggedEdgeId = attachments[draggedAttachmentId].edgeId;
            var draggedEdge = draggedEdgeId && edges[draggedEdgeId];
            var isDraggingToOriginalPosition = draggedEdge &&
                targetAttachmentId &&
                [draggedEdge.fromAttachmentId, draggedEdge.toAttachmentId].includes(targetAttachmentId);
            if (targetAttachmentId &&
                draggedEdgeId &&
                !isDraggingToOriginalPosition) {
                var targetAttachment_1 = attachments[targetAttachmentId];
                // deleting potential edge at target, to allow dragging edges to occupied slots
                var existingEdgeAtTarget = Object.values(edges).find(function (edge) {
                    return edge.fromAttachmentId === targetAttachment_1.id ||
                        edge.toAttachmentId === targetAttachment_1.id;
                });
                // deleting the dragged edge to make room for a new version of that edge
                dispatch(actions.deleteEdge(draggedEdgeId));
                if (existingEdgeAtTarget) {
                    dispatch(actions.deleteEdge(existingEdgeAtTarget.id));
                }
                dispatch(actions.addEdge(draggedAttachmentId, targetAttachment_1.id));
                dispatch(actions.deselectEntities());
            }
            dispatch(actions.setDraggedAttachmentId(null));
            return;
        }
        // User is dragging a selection rectangle
        if (selectionRectangle) {
            stopSelection();
            var _b = getPickerArea(selectionRectangle), x = _b.x, y = _b.y, width = _b.width, height = _b.height;
            // if the area is too small then it's not considered a selection
            if (width < 5 || height < 5)
                return;
            var pickedEntities = pick({ x: x, y: y, width: width, height: height }, camera);
            if (!pickedEntities)
                return;
            var isSelectable = function (_a) {
                var type = _a.type;
                return type !== EntityType.OTHER;
            };
            var selectedIds = pickedEntities
                .filter(isSelectable)
                .map(function (_a) {
                var id = _a.id;
                return id;
            });
            if (!selectedIds.length)
                return;
            dispatch(actions.selectEntities(selectedIds));
            return;
        }
        // User is picking nothing, nothing to do
        if (!picked)
            return;
        var pickedOnMouseUp = picked;
        setPicked(null);
        /* What a finished drag persists — and what a plain click means — is the
         * picked kind's business; see `./drag-behaviors`. */
        var behavior = dragBehaviors[pickedOnMouseUp.type];
        if (behavior && dragContext) {
            if (isEntityMoving) {
                setIsEntityMoving(false);
                behavior.commitDrag(pickedOnMouseUp, dragContext);
                if (behavior.handlesSelection)
                    return;
                // if multi-selecting and moving nodes, early exit to avoid deselecting
                if (isMultiSelecting)
                    return;
            }
            else if (behavior.clickWithoutDrag) {
                behavior.clickWithoutDrag(pickedOnMouseUp, dragContext);
                return;
            }
        }
        if (multiSelectKeyPressed) {
            // cannot multi-select when an attachment is currently selected. early exit
            if (attachments[currentlySelectedId])
                return;
            toggleEntitySelection(picked.id);
            return;
        }
        dispatch(actions.selectEntity(picked.id));
    }, [
        cupFillLayout,
        draggedAttachmentId,
        selectionRectangle,
        picked,
        isEntityMoving,
        dragContext,
        multiSelectKeyPressed,
        dispatch,
        pick,
        attachments,
        edges,
        stopSelection,
        isMultiSelecting,
        currentlySelectedId,
        toggleEntitySelection,
    ]);
    var getDistanceToAttachment = useCallback(function (x, y, attachment, cameraEntity) {
        var nodePos = nodes[attachment.nodeId].position;
        var cameraHeight = cameraEntity.getPosition().y - nodePos.y;
        var pointerPos = cameraEntity.camera.screenToWorld(x, y, cameraHeight);
        return Math.hypot(pointerPos.x - nodePos.x, pointerPos.z - nodePos.z);
    }, [nodes]);
    var onMouseMove = useCallback(function (eventX, eventY, cameraEntity) {
        var _a;
        if (!app || !picker || !cameraEntity.camera)
            return;
        /* Armed fill tool: nothing in the scene is hovered or dragged, the
         * pointer only says where the grid will end. */
        if (cupFillLayout) {
            hoveredId && setHoveredId(undefined);
            document.body.style.cursor = isWithinCanvas(app, eventX, eventY)
                ? "crosshair"
                : "auto";
            return;
        }
        if (selectionRectangle) {
            updateSelection({ x: eventX, y: eventY });
            return;
        }
        if (draggedAttachmentId && cameraEntity.camera) {
            var entityAtMouseLocation = pick({ x: eventX, y: eventY }, cameraEntity.camera);
            var targetAttachmentId = (_a = entityAtMouseLocation === null || entityAtMouseLocation === void 0 ? void 0 : entityAtMouseLocation.find(function (p) { return p.type === EntityType.ATTACHMENT; })) === null || _a === void 0 ? void 0 : _a.id;
            dispatch(actions.setHoveredAttachmentId(targetAttachmentId || null));
        }
        // update hover info when dragging an edge or when nothing is picked
        if (!picked || draggedAttachmentId) {
            var pickedInfo = pick({ x: eventX, y: eventY }, cameraEntity.camera);
            if (!(pickedInfo === null || pickedInfo === void 0 ? void 0 : pickedInfo.length))
                return;
            var _b = pickedInfo[0], id = _b.id, type = _b.type;
            var isSelectedNodeHovered = currentlySelectedIds.includes(id);
            var isUnselectedNodeHovered = type !== EntityType.OTHER && !currentlySelectedIds.includes(id);
            var cursor = void 0;
            if (isSelectedNodeHovered) {
                cursor = isMultiSelecting ? "pointer" : "move";
                hoveredId !== id && setHoveredId(id);
            }
            else if (isUnselectedNodeHovered) {
                cursor = "pointer";
                hoveredId !== id && setHoveredId(id);
            }
            else {
                cursor = "auto";
                hoveredId && setHoveredId(undefined);
            }
            document.body.style.cursor = cursor;
        }
        // if we are starting to drag an edge, calculate which attachment should be disconnected
        if ((picked === null || picked === void 0 ? void 0 : picked.type) === EntityType.EDGE &&
            draggedAttachmentId === null &&
            edges[picked.id]) {
            var pickedEdge = edges[picked.id];
            var fromAttachment = attachments[pickedEdge.fromAttachmentId];
            var toAttachment = attachments[pickedEdge.toAttachmentId];
            var fromAttachmentDistance = getDistanceToAttachment(eventX, eventY, fromAttachment, cameraEntity);
            var toAttachmentDistance = getDistanceToAttachment(eventX, eventY, toAttachment, cameraEntity);
            // disconnect the attachment that is furthest from the pointer
            var furthestAttachment = fromAttachmentDistance >= toAttachmentDistance
                ? fromAttachment
                : toAttachment;
            dispatch(actions.setDraggedAttachmentId(furthestAttachment.id));
            setPicked(null);
            return;
        }
        if (!initialY || !picked)
            return;
        var behavior = dragBehaviors[picked.type];
        if (!behavior || !dragContext)
            return;
        /* A press that has not travelled far enough yet is still a click, so the
         * entity stays put. Once it has, the drag is latched for the rest of the
         * press so a slow pointer doesn't drop back into clicking. */
        if (!isEntityMoving) {
            var travelled = pointerDownScreen
                ? Math.hypot(eventX - pointerDownScreen.x, eventY - pointerDownScreen.y)
                : Infinity;
            if (travelled < dragThresholdPixels)
                return;
            setIsEntityMoving(true);
        }
        var _c = screenToWorldAtY(cameraEntity.camera, eventX, eventY, initialY), x = _c.x, z = _c.z;
        // Everything that travels with the pointer, mapped to where it stands
        // now — which kind of entity brings which companions is the drag
        // behavior's business.
        var pickedPosition = picked.entity.getPosition().clone();
        var entityPositionMap = new Map(behavior
            .collectDragTargets(picked, dragContext)
            .map(function (entity) { return [entity, entity.getPosition().clone()]; }));
        // Calculate the movement offset from the picked entity. The grab offset
        // keeps the pointer at the same spot on the entity it grabbed, so the
        // entity follows the pointer without snapping its origin to it.
        var offsetX = x + grabOffset.x - pickedPosition.x;
        var offsetY = initialY - pickedPosition.y;
        var offsetZ = z + grabOffset.z - pickedPosition.z;
        // update the position of all entities with the same offset
        entityPositionMap.forEach(function (position, entity) {
            var newX = position.x + offsetX;
            var newY = position.y + offsetY;
            var newZ = position.z + offsetZ;
            // Teleport bodies with a rigidbody (e.g. a box's collision proxy) so
            // the physics transform stays in sync and cup-over-box raycasts
            // follow the box during the drag. Plain entities just move.
            var rigidbody = entity.rigidbody;
            if (rigidbody) {
                rigidbody.teleport(newX, newY, newZ);
            }
            else {
                entity.setPosition(newX, newY, newZ);
            }
        });
    }, [
        app,
        picker,
        cupFillLayout,
        selectionRectangle,
        picked,
        draggedAttachmentId,
        edges,
        initialY,
        grabOffset,
        isEntityMoving,
        pointerDownScreen,
        currentlySelectedIds,
        dragContext,
        updateSelection,
        pick,
        isMultiSelecting,
        hoveredId,
        attachments,
        getDistanceToAttachment,
        dispatch,
    ]);
    useEffect(function () {
        if (!app)
            return;
        var canvas = app.graphicsDevice.canvas;
        setPicker(new pc.Picker(app, canvas.clientWidth, canvas.clientHeight));
    }, [app]);
    useEffect(function () {
        if (!picker || !app)
            return;
        var cameraEntity = app.root.findByName("Camera");
        if (!cameraEntity || !cameraEntity.camera)
            return;
        var mouseMove = function (event) {
            return onMouseMove(event.x, event.y, cameraEntity);
        };
        var mouseDown = function (event) {
            onMouseDown(event, cameraEntity.camera);
        };
        var mouseUp = function (event) {
            return onMouseUp(event, cameraEntity.camera);
        };
        if (!isDisabled) {
            app.mouse.on(pc.EVENT_MOUSEDOWN, mouseDown);
            app.mouse.on(pc.EVENT_MOUSEMOVE, mouseMove);
            app.mouse.on(pc.EVENT_MOUSEUP, mouseUp);
        }
        return function () {
            app.mouse.off(pc.EVENT_MOUSEDOWN, mouseDown);
            app.mouse.off(pc.EVENT_MOUSEMOVE, mouseMove);
            app.mouse.off(pc.EVENT_MOUSEUP, mouseUp);
        };
    }, [picker, app, onMouseDown, onMouseMove, onMouseUp, isDisabled]);
    var movingNodeIds = useMemo(function () {
        var _a, _b;
        if (!isEntityMoving || !picked || !dragContext)
            return [];
        return ((_b = (_a = dragBehaviors[picked.type]) === null || _a === void 0 ? void 0 : _a.movingNodeIds(picked, dragContext)) !== null && _b !== void 0 ? _b : __spreadArray([], __read(currentlySelectedIds), false));
    }, [currentlySelectedIds, isEntityMoving, picked, dragContext]);
    return {
        picker: picker,
        setPickerDisabled: setPickerDisabled,
        movingNodeIds: movingNodeIds,
        hoverId: hoveredId,
        selectionRectangle: selectionRectangle,
    };
};
/**
 * Gets a picker area from a selection rectangle.
 * Can handle cases where the user drags the selection rectangle "the wrong way",
 * I.E. from right to left or from bottom to top.
 * The picker area can be used to pick entities with pc.Picker.
 */
export function getPickerArea(selectionRectangle) {
    var x = Math.min(selectionRectangle.from.x, selectionRectangle.to.x);
    var y = Math.min(selectionRectangle.from.y, selectionRectangle.to.y);
    var width = Math.abs(selectionRectangle.to.x - selectionRectangle.from.x);
    var height = Math.abs(selectionRectangle.to.y - selectionRectangle.from.y);
    return { x: x, y: y, width: width, height: height };
}
/**
 * Projects a screen-space point onto the horizontal plane at the given world
 * y value, returning the world x/z coordinates.
 *
 * `screenToWorld` returns a point whose y varies with the screen distance from
 * the picked point, so we recalculate the intersection using the line passing
 * between the camera position and the returned point.
 *
 * Direction vector <L, M, N> = <x1-x2, y1-y2, z1-z2>
 *   x(t) = x1 + Lt,  y(t) = y1 + Mt,  z(t) = z1 + Nt
 * We know the target y, so we solve for t and then x and z.
 */
export function screenToWorldAtY(camera, screenX, screenY, targetY) {
    var cameraPos = camera.entity.getPosition();
    var pos = camera.screenToWorld(screenX, screenY, cameraPos.y - targetY);
    var l = pos.x - cameraPos.x;
    var m = pos.y - cameraPos.y;
    var n = pos.z - cameraPos.z;
    var t = (targetY - pos.y) / m;
    return { x: pos.x + l * t, z: pos.z + n * t };
}
/*
 * Whether canvas-relative pointer coordinates fall within the canvas. PlayCanvas
 * listens on the window, so a press on the sidebar reaches the picker too, with
 * coordinates outside the canvas.
 */
export function isWithinCanvas(app, x, y) {
    var canvas = app.graphicsDevice.canvas;
    return (x >= 0 && y >= 0 && x <= canvas.clientWidth && y <= canvas.clientHeight);
}
/*
 * Whether a pointer event happened on the canvas itself — not on something
 * drawn over it. The topbar (2D/3D, undo), the checklist and the in-scene
 * panels all lie within the canvas bounds, so bounds alone would take a click
 * on the 3D button for a click in the scene; the native event's target tells
 * them apart. Falls back to the bounds where there is no native event to ask.
 */
export function isPointerOnCanvas(app, event) {
    var _a;
    var target = (_a = event.event) === null || _a === void 0 ? void 0 : _a.target;
    if (target instanceof Element)
        return target === app.graphicsDevice.canvas;
    return isWithinCanvas(app, event.x, event.y);
}
/*
 * Spread onto a panel drawn over the canvas so a press on it is not also taken
 * for a press in the scene behind it (which would pick or deselect a node).
 */
export var ignoreScenePressProps = { "data-ignore-scene-press": "" };
function isPressOnIgnoredElement(event) {
    var _a;
    var target = (_a = event.event) === null || _a === void 0 ? void 0 : _a.target;
    return (target instanceof Element &&
        target.closest("[data-ignore-scene-press]") !== null);
}
/**
 * Prepares the picker to pick entities in the scene.
 */
function preparePicker(picker, app, camera) {
    var canvas = app.graphicsDevice.canvas;
    picker.resize(canvas.clientWidth, canvas.clientHeight);
    picker.prepare(camera, app.scene);
}
